
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlarmClockMinus, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DeductHoursButtonProps {
  reportId: string;
  onSuccess?: () => void;
}

export const DeductHoursButton = ({ reportId, onSuccess }: DeductHoursButtonProps) => {
  const [isDeducting, setIsDeducting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();

  const handleDeductHours = async () => {
    try {
      setIsDeducting(true);
      
      // Call the edge function to deduct hours
      const { data, error } = await supabase.functions.invoke('deduct-student-hours', {
        body: { reportId }
      });
      
      if (error) {
        console.error('Error deducting hours:', error);
        toast({
          variant: 'destructive',
          title: 'การหักชั่วโมงล้มเหลว',
          description: error.message || 'เกิดข้อผิดพลาดในการหักชั่วโมงเรียน'
        });
        return;
      }
      
      if (data.success) {
        toast({
          title: 'หักชั่วโมงเรียนสำเร็จ',
          description: 'ชั่วโมงเรียนของนักเรียนถูกหักเรียบร้อยแล้ว',
        });
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast({
          variant: 'destructive',
          title: 'การหักชั่วโมงล้มเหลว',
          description: data.message || 'ไม่สามารถหักชั่วโมงเรียนได้'
        });
      }
    } catch (error) {
      console.error('Error deducting hours:', error);
      toast({
        variant: 'destructive',
        title: 'การหักชั่วโมงล้มเหลว',
        description: 'เกิดข้อผิดพลาดในระบบ โปรดลองใหม่อีกครั้ง'
      });
    } finally {
      setIsDeducting(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => setIsOpen(true)}
        disabled={isDeducting}
        className="hover:text-amber-600 border-amber-400 hover:bg-amber-50 text-amber-500"
      >
        {isDeducting ? (
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
        ) : (
          <AlarmClockMinus className="h-4 w-4 mr-1" />
        )}
        หักชั่วโมง
      </Button>

      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการหักชั่วโมงเรียน</AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการยืนยันการหักชั่วโมงเรียนของนักเรียนใช่หรือไม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeductHours}
              disabled={isDeducting}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {isDeducting ? 'กำลังดำเนินการ...' : 'ยืนยันการหักชั่วโมง'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
